// CTV-611 rework + CTV-785 review: a short-lived Waymark editor session on an
// AI commercial that is NOT the surface's active one.
//
// Deliberately NOT a lift of WaymarkProvider: the provider owns the full
// account/generation lifecycle of the ACTIVE commercial (the newest
// uncommitted one) for the life of the surface. This hook only reopens an
// already-rendered video so the buyer can change it, then hands the new
// broadcast-quality file back to the caller:
//   - openForCreative (CTV-611 rework): a saved creative that was rejected.
//     POST /waymark/login-for-creative mints the login token (authenticated).
//   - openForCommercial (CTV-785 review): a committed commercial on the wizard
//     or the Edit commercial tab. POST /waymark/token with the commercial's
//     own access token, the same call the provider makes.
//   - openForStudioCommercial (CTV-800): a studio commercial that is not the
//     Studio's active one. POST /admin/waymark/commercials/:id/token,
//     authenticated and keyed on the row id.
// Both responses carry the commercial's current videoId and renderedUrl from
// the server, so a session never depends on the surface's in-memory snapshot
// (which goes stale the moment the provider persists state for a commercial
// created in this session).
//
// Until CTV-785, Review on a committed slot re-pointed the provider at that
// commercial: it tore down the active one's generation subscriptions and, on
// the way back, restarted its generation from the stale snapshot. A side
// session leaves the active commercial alone. Two SDK instances then share
// the page, and the SDK broadcasts its events on `document`, so every handler
// here consults editorSessions.ts (and the provider does the same).
//
// Flow: mint token, bootstrap the SDK exactly like the provider (same
// constructor + createAccount/loginAccount branch), openEditorForVideo, the
// buyer clicks Finish (videoCompleted: phase 'uploading'), the broadcast
// render lands (videoRendered, chosen by pickBroadcastRender so a stale
// render is never mistaken for the new one), fetch it as a Blob, ad-spec
// gate, wrap in a File, onFileReady. The SDK module is imported lazily so it
// stays out of the modal's initial bundle (and out of jsdom test runs).

import React from 'react';
import { apiClient, ApiError } from '../../../lib/apiClient';
import { validateAdSpecs, formatAdSpecViolations } from '../shared/validateAdSpecs';
import type { EditorSession, IssueTokenResponse } from './types';
import { pickBroadcastRender } from './pickBroadcastRender';
import {
  claimEditor,
  editorOwnedByOther,
  registerSessionVideo,
  releaseEditor,
  unregisterSessionVideo,
} from './editorSessions';

type WaymarkInstance = InstanceType<(typeof import('@waymark/waymark-sdk'))['default']>;

// Response of POST /waymark/login-for-creative: the /waymark/token shape with
// the videoId guaranteed (the endpoint 409s without one).
interface LoginForCreativeResponse extends IssueTokenResponse {
  videoId: string;
}

export interface UseWaymarkEditorSessionArgs {
  /**
   * Called with the broadcast-quality render once the buyer clicks Finish in
   * the editor and the file passes the ad-spec gate. `waymarkCommercialId`
   * echoes the id passed to the open call so the caller can route the file:
   * replace the slot that carries the commercial, or stamp a new slot.
   */
  onFileReady: (file: File, waymarkCommercialId: string) => void;
}

export interface UseWaymarkEditorSessionResult {
  /** CTV-611: reopen a saved creative's AI commercial (authenticated). */
  openForCreative: (args: { creativeId: string; waymarkCommercialId: string }) => Promise<void>;
  /** CTV-785: reopen a committed commercial by its own access token. */
  openForCommercial: (args: {
    commercialId: string;
    commercialToken: string;
    slug: string;
  }) => Promise<void>;
  /**
   * CTV-800: reopen a STUDIO commercial that is not the Studio's active one.
   * Authenticated and keyed on the row id, because a studio row's access
   * token never reaches the browser. Everything after the token mint (the SDK
   * bootstrap, the editorSessions arbitration, the render pick, the ad-spec
   * gate) is the shared path the two buyer openers already use.
   */
  openForStudioCommercial: (args: { commercialId: string }) => Promise<void>;
  /** True while the token round-trip + SDK bootstrap is in flight. */
  isBusy: boolean;
  isEditorOpen: boolean;
  /** The commercial this session is on and where it is; null between sessions. */
  session: EditorSession | null;
  /**
   * Creatives whose AI commercial can no longer be reopened (409
   * waymark_video_unavailable, or 404). Callers hide/disable the Waymark
   * action for these and point the buyer at the upload path instead.
   */
  unavailableCreativeIds: ReadonlySet<string>;
  /** Commercials (review path) that can no longer be reopened. */
  unavailableCommercialIds: ReadonlySet<string>;
  error: string | null;
}

// Mirrors WaymarkProvider's hidden 1x1 off-screen container styling.
const HIDDEN_CONTAINER_STYLE: Partial<CSSStyleDeclaration> = {
  position: 'absolute',
  left: '-99999px',
  top: '-99999px',
  transform: 'none',
  width: '1px',
  height: '1px',
  zIndex: '-1',
  pointerEvents: 'none',
};

// Mirrors WaymarkProvider's centered editor window (~90% viewport over a
// dimmed backdrop, not a fullscreen takeover).
const OPEN_CONTAINER_STYLE: Partial<CSSStyleDeclaration> = {
  position: 'fixed',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 'min(1100px, 92vw)',
  height: 'min(720px, 88vh)',
  borderRadius: '12px',
  overflow: 'hidden',
  boxShadow: '0 25px 60px -15px rgba(15, 23, 42, 0.45)',
  background: '#fff',
  zIndex: '1000',
  pointerEvents: 'auto',
};

// Waymark fires editorExited for Exit and for Finish alike. On Finish the
// videoCompleted event is the signal that a render is coming; this grace
// window tolerates either ordering of the two events before an exit is
// treated as "left without finishing".
const EXIT_GRACE_MS = 1500;

function isVideoUnavailable(body: unknown): boolean {
  return (
    typeof body === 'object' &&
    body !== null &&
    (body as { code?: unknown }).code === 'waymark_video_unavailable'
  );
}

interface LiveSession {
  commercialId: string;
  videoId: string;
  ownerId: string;
  /** The broadcast file the commercial already had when the session opened. */
  previousUrl: string | null;
  /** Epoch ms of the Finish click; null until videoCompleted. */
  since: number | null;
  exitTimer: number | null;
}

export function useWaymarkEditorSession({
  onFileReady,
}: UseWaymarkEditorSessionArgs): UseWaymarkEditorSessionResult {
  const [isBusy, setIsBusy] = React.useState(false);
  const [isEditorOpen, setIsEditorOpen] = React.useState(false);
  const [session, setSession] = React.useState<EditorSession | null>(null);
  const [unavailableCreativeIds, setUnavailableCreativeIds] = React.useState<ReadonlySet<string>>(
    () => new Set(),
  );
  const [unavailableCommercialIds, setUnavailableCommercialIds] = React.useState<
    ReadonlySet<string>
  >(() => new Set());
  const [error, setError] = React.useState<string | null>(null);

  const sdkRef = React.useRef<WaymarkInstance | null>(null);
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const backdropRef = React.useRef<HTMLDivElement | null>(null);
  const liveRef = React.useRef<LiveSession | null>(null);
  // videoRendered can fire more than once for the same broadcast-quality URL;
  // process each URL once per session.
  const processedUrlRef = React.useRef<string | null>(null);
  const onFileReadyRef = React.useRef(onFileReady);
  onFileReadyRef.current = onFileReady;

  const hideEditor = React.useCallback(() => {
    if (backdropRef.current) {
      backdropRef.current.remove();
      backdropRef.current = null;
    }
    if (containerRef.current) {
      Object.assign(containerRef.current.style, HIDDEN_CONTAINER_STYLE);
    }
    setIsEditorOpen(false);
  }, []);

  const closeEditor = React.useCallback(async () => {
    try {
      await sdkRef.current?.close();
    } catch {
      // The editor iframe may already be gone; hiding the overlay is what matters.
    }
    hideEditor();
  }, [hideEditor]);

  const showEditor = React.useCallback(() => {
    const container = containerRef.current;
    if (!container) return;
    if (!backdropRef.current) {
      const backdrop = document.createElement('div');
      backdrop.setAttribute('aria-hidden', 'true');
      Object.assign(backdrop.style, {
        position: 'fixed',
        inset: '0',
        background: 'rgba(15, 23, 42, 0.55)',
        zIndex: '999',
      });
      backdrop.addEventListener('click', () => {
        void closeEditor();
      });
      document.body.appendChild(backdrop);
      backdropRef.current = backdrop;
    }
    Object.assign(container.style, OPEN_CONTAINER_STYLE);
    setIsEditorOpen(true);
  }, [closeEditor]);

  const ensureContainer = React.useCallback((): HTMLDivElement => {
    if (containerRef.current) return containerRef.current;
    const el = document.createElement('div');
    el.setAttribute('data-waymark-editor-session-container', '');
    Object.assign(el.style, HIDDEN_CONTAINER_STYLE);
    document.body.appendChild(el);
    containerRef.current = el;
    return el;
  }, []);

  // Forget the live session: the arbiter stops shielding its video, the
  // editor claim is dropped, and the receipt row goes back to rest.
  const endSession = React.useCallback(() => {
    const live = liveRef.current;
    if (live) {
      if (live.exitTimer != null) window.clearTimeout(live.exitTimer);
      unregisterSessionVideo(live.videoId);
      releaseEditor(live.ownerId);
    }
    liveRef.current = null;
    setSession(null);
  }, []);

  // Teardown on unmount: cleanup the SDK and remove the imperative DOM nodes.
  React.useEffect(() => {
    return () => {
      const sdk = sdkRef.current;
      sdkRef.current = null;
      if (sdk) {
        void sdk.cleanup().catch(() => {
          /* noop */
        });
      }
      backdropRef.current?.remove();
      backdropRef.current = null;
      containerRef.current?.remove();
      containerRef.current = null;
      endSession();
    };
  }, [endSession]);

  const handleRendered = React.useCallback(
    async (renderedUrl: string, videoId: string, waymarkCommercialId: string) => {
      if (processedUrlRef.current === renderedUrl) return;
      processedUrlRef.current = renderedUrl;
      try {
        const res = await fetch(renderedUrl);
        if (!res.ok) {
          setError(`Could not download the updated commercial (${res.status}). Please try again.`);
          return;
        }
        const blob = await res.blob();
        // Same client-side ad-spec gate the surfaces apply before a Waymark
        // render enters the upload pipeline.
        const specs = await validateAdSpecs(blob);
        if (!specs.ok) {
          setError(formatAdSpecViolations(specs.violations));
          return;
        }
        const file = new File([blob], `waymark-${videoId}.mp4`, { type: 'video/mp4' });
        onFileReadyRef.current(file, waymarkCommercialId);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Could not download the updated commercial');
      } finally {
        endSession();
      }
    },
    [endSession],
  );

  const openSession = React.useCallback(
    async (
      commercialId: string,
      mintAuth: () => Promise<IssueTokenResponse>,
      onUnavailable: () => void,
    ) => {
      setError(null);
      setIsBusy(true);
      // A new open supersedes whatever session was live.
      endSession();
      setSession({ commercialId, phase: 'opening' });
      const ownerId = `editor-session:${commercialId}`;
      try {
        const auth = await mintAuth();
        if (!auth.videoId) {
          // The commercial never captured a Waymark video (generation never
          // completed, or its state was reset): nothing to reopen.
          onUnavailable();
          endSession();
          return;
        }
        const videoId = auth.videoId;

        const { default: Waymark } = await import('@waymark/waymark-sdk');
        const container = ensureContainer();

        // One SDK per session: each commercial has its own Waymark account, so
        // a cached login can't be reused across commercials.
        const prior = sdkRef.current;
        if (prior) {
          sdkRef.current = null;
          try {
            await prior.cleanup();
          } catch {
            /* noop */
          }
        }

        const sdk = new Waymark(auth.partnerID, {
          authMode: 'jwt',
          environment: auth.environment,
          domElement: container,
        });
        if (auth.mode === 'create') {
          await sdk.createAccount(auth.token);
        } else {
          await sdk.loginAccount(auth.token);
        }

        const live: LiveSession = {
          commercialId,
          videoId,
          ownerId,
          previousUrl: auth.renderedUrl ?? null,
          since: null,
          exitTimer: null,
        };
        liveRef.current = live;
        processedUrlRef.current = null;
        registerSessionVideo(videoId);
        const stillLive = () => liveRef.current === live;

        sdk.on('videoCompleted', (videoData) => {
          if (!stillLive() || editorOwnedByOther(ownerId)) return;
          if (videoData.id !== live.videoId) return;
          if (live.exitTimer != null) {
            window.clearTimeout(live.exitTimer);
            live.exitTimer = null;
          }
          live.since = Date.now();
          setSession({ commercialId, phase: 'uploading' });
        });

        sdk.on('videoRendered', (videoData) => {
          if (!stillLive()) return;
          if (videoData.id !== live.videoId) return;
          const rendered = pickBroadcastRender(videoData.renders ?? [], {
            previousUrl: live.previousUrl,
            since: live.since,
          });
          if (!rendered) return;
          void handleRendered(rendered.url, videoData.id, commercialId);
          void closeEditor();
        });

        sdk.on('editorExited', () => {
          if (!stillLive() || editorOwnedByOther(ownerId)) return;
          hideEditor();
          // After Finish the render is still on its way; an exit without
          // Finish ends the session once the grace window confirms it.
          if (live.since != null || live.exitTimer != null) return;
          live.exitTimer = window.setTimeout(() => {
            live.exitTimer = null;
            if (stillLive() && live.since == null) endSession();
          }, EXIT_GRACE_MS);
        });
        sdkRef.current = sdk;

        claimEditor(ownerId);
        await sdk.openEditorForVideo(videoId);
        showEditor();
        setSession({ commercialId, phase: 'editing' });
      } catch (e) {
        if (
          e instanceof ApiError &&
          (e.status === 404 || (e.status === 409 && isVideoUnavailable(e.body)))
        ) {
          onUnavailable();
        } else {
          setError(e instanceof Error ? e.message : 'Could not open the Waymark editor');
        }
        hideEditor();
        endSession();
      } finally {
        setIsBusy(false);
      }
    },
    [closeEditor, endSession, ensureContainer, handleRendered, hideEditor, showEditor],
  );

  const openForCreative = React.useCallback(
    ({ creativeId, waymarkCommercialId }: { creativeId: string; waymarkCommercialId: string }) =>
      openSession(
        waymarkCommercialId,
        () => apiClient.post<LoginForCreativeResponse>('/waymark/login-for-creative', { creativeId }),
        () =>
          setUnavailableCreativeIds((prev) => {
            const next = new Set(prev);
            next.add(creativeId);
            return next;
          }),
      ),
    [openSession],
  );

  const openForCommercial = React.useCallback(
    ({
      commercialId,
      commercialToken,
      slug,
    }: {
      commercialId: string;
      commercialToken: string;
      slug: string;
    }) =>
      openSession(
        commercialId,
        () => apiClient.post<IssueTokenResponse>('/waymark/token', { commercialToken, slug }),
        () => {
          setUnavailableCommercialIds((prev) => {
            const next = new Set(prev);
            next.add(commercialId);
            return next;
          });
          setError('This commercial can no longer be opened in the editor.');
        },
      ),
    [openSession],
  );

  const openForStudioCommercial = React.useCallback(
    ({ commercialId }: { commercialId: string }) =>
      openSession(
        commercialId,
        () =>
          apiClient.post<IssueTokenResponse>(
            `/admin/waymark/commercials/${commercialId}/token`,
            {},
          ),
        () => {
          setUnavailableCommercialIds((prev) => {
            const next = new Set(prev);
            next.add(commercialId);
            return next;
          });
          setError('This commercial can no longer be opened in the editor.');
        },
      ),
    [openSession],
  );

  return {
    openForCreative,
    openForCommercial,
    openForStudioCommercial,
    isBusy,
    isEditorOpen,
    session,
    unavailableCreativeIds,
    unavailableCommercialIds,
    error,
  };
}
